/* The renderer. The body is a stack of copies of its outline, spaced along
   a depth axis with a pillow profile (smaller at the caps); each copy is
   projected with the head's yaw and pitch, so the stack reads as a rounded
   extruded solid that turns, flips and shows its side. The plastic
   material (plastic.ts) lights it; the face lives on the front cap and
   follows it. */

import type { Pose } from './Sim';
import { shade } from './color';
import { BODY, drawPlasticCap, HIGHLIGHT, LX, LY, mulAffine, RIM, SHADOW, SPREAD } from './plastic';

export interface DrawConfig {
    path: Path2D;
    /** thin parts (antennae), drawn behind the body with a fraction of its depth */
    parts: Path2D;
    /** identifies the outline for the material caches */
    key: string;
    /** where the face sits, in the 100×100 body box, and its scale */
    faceX: number;
    faceY: number;
    faceScale: number;
    /** no animation loop follows this draw (paused, reduced motion): build
        the material now instead of on idle time */
    still: boolean;
}

/* The canvas is drawn larger than the avatar's layout box, so a hop or a
   flip can leave the box without being clipped. */
export const OVERSCAN = 1.5;
/** the body's centre sits this fraction of the box below the canvas
    centre: hops and flips need the room above, not below */
export const RISE = 0.1;

/* Copies through the depth, and the half-depth in body units: the stock
   15 at 0.7 of the stock thickness. */
const SLICES = 17;
const HALF_DEPTH = 15 * 0.7;
/* Cap scale at the ends of the pillow, at the stock rim width. */
const CAP = 0.9;
const profile = (z: number, cap: number) => cap + (1 - cap) * Math.sqrt(Math.max(0, 1 - z * z));
const PARTS_DEPTH = 0.4;

/* Big, plain white eyes: they carry the face at 24px. */
const INK = '#fff';
const EYE_GAP = 25;
const EYE_RX = 6.3;
const EYE_Y = 1;

/* the numbers of an hsl() string; any other colour is normalised through shade() first */
const hslNums = (c: string) => (c.startsWith('hsl(') ? c : shade(c, 0)).match(/[\d.]+/g)!.map(Number);
function mixCss(a: string, b: string, t: number): string {
    const pa = hslNums(a);
    const pb = hslNums(b);
    const m = pa.map((v, i) => v + (pb[i] - v) * t);
    return `hsl(${m[0].toFixed(1)} ${m[1].toFixed(1)}% ${m[2].toFixed(1)}%)`;
}

/* While the plastic form is still baking on idle time, the slices stand in
   with one colour ramp through the depth to the front. */
const FAR = shade(BODY, -0.3 * SHADOW, 0.05 * SHADOW);
const SMOOTH_RAMP = Array.from({ length: SLICES }, (_, j) => {
    const t = j / (SLICES - 1);
    return t >= 0.5 ? BODY : mixCss(FAR, BODY, t / 0.5);
});

/**
 * Draw one frame. `box` is the avatar's layout size in CSS px; the canvas
 * is `box * OVERSCAN` square with the body's centre `RISE * box` below
 * its middle, and the context already scaled for the device pixel ratio.
 */
export function draw(ctx: CanvasRenderingContext2D, box: number, dpr: number, pose: Pose, cfg: DrawConfig) {
    const full = box * OVERSCAN;
    ctx.clearRect(0, 0, full, full);
    const S = box / 100;
    /* the context's transform as given (the device scale) is the base of
       every transform set here */
    const base = [dpr, 0, 0, dpr, 0, 0];
    const cap = 1 - (1 - CAP) * RIM;

    const cy0 = Math.cos(pose.yaw),
        sy = Math.sin(pose.yaw);
    const cp0 = Math.cos(pose.pitch),
        sp = Math.sin(pose.pitch);
    /* which cap faces the viewer: the front while this is positive */
    const facing = cy0 * cp0;
    /* edge-on, every slice would thin to a line and the stack would show
       gaps; a floor on the foreshortening keeps it a solid */
    const floor = (v: number) => (Math.abs(v) < 0.22 ? (v < 0 ? -0.22 : 0.22) : v);
    const cy = floor(cy0),
        cp = floor(cp0);

    /* body space: the box centre plus the pose's offset, its roll and
       squash. The squash and stretch scale about the body's base (y = 50),
       so a landing keeps the feet on the ground and presses the top down,
       and a stretch rises from the base. */
    const cr = Math.cos(pose.roll),
        sr = Math.sin(pose.roll),
        kx = pose.sx * S,
        ky = pose.sy * S;
    const lift = 50 * (1 - pose.sy) * S;
    const body = mulAffine(base, [
        cr * kx,
        sr * kx,
        -sr * ky,
        cr * ky,
        full / 2 - sr * lift,
        full / 2 + RISE * box + pose.y * S + cr * lift,
    ]);
    ctx.save();
    ctx.setTransform(body[0], body[1], body[2], body[3], body[4], body[5]);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    /* one solid: the plastic material, or while its form is baking the
       stock slices with a soft shadow and light laid over them */
    const drawSolid = (path: Path2D, key: string, halfDepth: number): boolean => {
        const rig = { cy, sy, cp, sp, facing, roll: pose.roll, halfDepth, cap, dev: box * dpr, ctm: body };
        if (drawPlasticCap(ctx, path, key, { ...rig, still: cfg.still })) return true;

        const union = new Path2D();
        /* slices, far to near; each slice's affine is applied relative to
           the previous slice's: one transform() per slice, no save/restore */
        const order = facing >= 0 ? 1 : -1;
        const [ca, cb, cc, cd, ce, cf] = body;
        let fill: string | null = null;
        let pa = 1,
            pb = 0,
            pc = 0,
            pd = 1,
            pe = 0,
            pf = 0;
        for (let j = 0; j < SLICES; j++) {
            const k = order > 0 ? j : SLICES - 1 - j;
            const z = -1 + (2 * k) / (SLICES - 1);
            const s = profile(z, cap);
            /* yaw about Y then pitch about X, orthographic: an affine per slice,
               then the path's own origin at its centre */
            const m0 = cy * s,
                m1 = sy * sp * s,
                m3 = cp * s;
            const e = z * sy * halfDepth - 50 * m0,
                fo = -z * cy * sp * halfDepth - 50 * m1 - 50 * m3;
            const det = pa * pd - pb * pc;
            const ia = pd / det,
                ib = -pb / det,
                ic = -pc / det,
                id = pa / det,
                ie = (pc * pf - pd * pe) / det,
                jf = (pb * pe - pa * pf) / det;
            ctx.transform(
                ia * m0 + ic * m1,
                ib * m0 + id * m1,
                ic * m3,
                id * m3,
                ia * e + ic * fo + ie,
                ib * e + id * fo + jf,
            );
            pa = m0;
            pb = m1;
            pc = 0;
            pd = m3;
            pe = e;
            pf = fo;
            const style = SMOOTH_RAMP[j];
            if (style !== fill) ctx.fillStyle = fill = style;
            ctx.fill(path);
            union.addPath(path, { a: m0, b: m1, c: 0, d: m3, e, f: fo });
        }
        ctx.setTransform(ca, cb, cc, cd, ce, cf);

        /* a soft shadow from the lower right and a light from the upper
           left, laid over the whole form so nothing has an edge; the
           gradients are made per frame on purpose: a kept one is slower to
           use in Safari than a fresh one */
        ctx.save();
        ctx.clip(union);
        const sa = Math.min(1, 0.34 * SHADOW);
        const sg = ctx.createRadialGradient(-LX * 45, -LY * 45, 4 * SPREAD, -LX * 45, -LY * 45, 84 * SPREAD);
        sg.addColorStop(0, `rgba(0,0,0,${sa})`);
        sg.addColorStop(0.5, `rgba(0,0,0,${sa * 0.35})`);
        sg.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'multiply';
        ctx.fillStyle = sg;
        ctx.fillRect(-120, -120, 240, 240);
        ctx.globalCompositeOperation = 'source-over';
        const ha = Math.min(1, 0.22 * HIGHLIGHT);
        const hg = ctx.createRadialGradient(LX * 37, LY * 37, 0, LX * 37, LY * 37, 62 * SPREAD);
        hg.addColorStop(0, `rgba(255,255,255,${ha})`);
        hg.addColorStop(0.6, `rgba(255,255,255,${ha * 0.23})`);
        hg.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = hg;
        ctx.fillRect(-120, -120, 240, 240);
        ctx.restore();
        return false;
    };

    drawSolid(cfg.parts, `${cfg.key}:parts`, HALF_DEPTH * PARTS_DEPTH);
    const plasticDone = drawSolid(cfg.path, cfg.key, HALF_DEPTH);

    /* the face: each feature sits on a sphere behind the front cap, so a
       turn slides it round the head — the eye moving toward the edge
       narrows, the other comes to the front, and past the side they go */
    if (facing > -0.2) {
        ctx.save();
        /* The face is printed on the front of the body, so it cannot leave
           it: clip to the front slice's own outline first. On a wide turn a
           feature's place on the sphere can reach past the body's foreshortened
           silhouette, and without this it floats off the side. */
        {
            const zf = facing >= 0 ? 1 : -1;
            const sf = profile(zf, cap);
            const m0 = cy * sf,
                m1 = sy * sp * sf,
                m3 = cp * sf;
            const e = zf * sy * HALF_DEPTH - 50 * m0;
            const fo = -zf * cy * sp * HALF_DEPTH - 50 * m1 - 50 * m3;
            const [ca, cb, cc, cd, ce, cf] = body;
            ctx.setTransform(
                ca * m0 + cc * m1,
                cb * m0 + cd * m1,
                cc * m3,
                cd * m3,
                ca * e + cc * fo + ce,
                cb * e + cd * fo + cf,
            );
            ctx.clip(cfg.path);
            ctx.setTransform(ca, cb, cc, cd, ce, cf);
        }
        ctx.translate(cfg.faceX - 50, cfg.faceY - 50);
        ctx.scale(cfg.faceScale, cfg.faceScale);
        /* under a clear coat the print shows the gloss faintly through it */
        if (plasticDone) ctx.globalAlpha = 0.93;
        drawFace(ctx, pose);
        ctx.restore();
    }
    ctx.restore();
}

/* Radius of the sphere the face is drawn on, in body units. */
const FACE_R = 30;

/* A feature's place on the sphere: longitude and latitude from its
   design position, turned by the head. Returns its screen offset, its
   foreshortening, and how much it faces the viewer. */
function onSphere(x: number, y: number, yaw: number, pitch: number) {
    const lon = Math.asin(Math.max(-1, Math.min(1, x / FACE_R))) + yaw;
    const lat = Math.asin(Math.max(-1, Math.min(1, -y / FACE_R))) + pitch;
    const cl = Math.cos(lat);
    return {
        x: FACE_R * Math.sin(lon) * cl,
        y: -FACE_R * Math.sin(lat),
        sx: Math.cos(lon),
        sy: cl,
        z: Math.cos(lon) * cl,
    };
}

/* An eye's curve as a short polyline with round joins, cached by its
   numbers. Stroking the open eye's hairpin curve directly gives a pill in
   Chrome but a teardrop in Safari, whose stroker does not round a cusp;
   both stroke a polyline the same way. */
const EYE_STEPS = 8;
const eyePaths = new Map<number, Path2D>();
function eyePath(x0: number, y0: number, cy: number): Path2D {
    const qx = Math.round(x0 * 50),
        qy = Math.round(y0 * 50),
        qc = Math.round(cy * 50);
    const key = qx + 2000 * qy + 4e6 * qc;
    let p = eyePaths.get(key);
    if (!p) {
        const ax = qx / 50,
            ay = qy / 50,
            ac = qc / 50;
        let d = `M${-ax} ${ay}`;
        for (let i = 1; i <= EYE_STEPS; i++) {
            const t = i / EYE_STEPS,
                mt = 1 - t;
            d += ` L${(mt * mt * -ax + t * t * ax).toFixed(3)} ${((mt * mt + t * t) * ay + 2 * mt * t * ac).toFixed(3)}`;
        }
        p = new Path2D(d);
        if (eyePaths.size > 256) eyePaths.clear();
        eyePaths.set(key, p);
    }
    return p;
}

function drawFace(ctx: CanvasRenderingContext2D, pose: Pose) {
    const [wd, ws] = pose.w;
    const half = EYE_GAP / 2;
    const lx = pose.lookX,
        ly = pose.lookY;
    const { yaw, pitch } = pose;

    /* place a feature: skip it once it has gone round the side */
    const at = (x: number, y: number, fn: () => void) => {
        const q = onSphere(x, y, yaw, pitch);
        if (q.z <= 0.02) return;
        ctx.save();
        ctx.globalAlpha = Math.min(1, q.z * 5);
        ctx.translate(q.x, q.y);
        ctx.scale(Math.max(0.02, q.sx), Math.max(0.02, q.sy));
        fn();
        ctx.restore();
    };

    /* Each eye is one stroked curve — endpoints at ±x0,y0, a control point
       at 0,cy, width w, round caps — so every look is the same shape with
       different numbers, and a blend of the numbers is a real morph: the
       upright pill of an open eye squashes into a shut line or droops into
       a sleeping lid. */
    /* the eyes take the head's aim, but only once it really aims
       somewhere: at rest and through the small drift of a breath they keep
       their own shape, and past that they draw taller looking up, shorter
       looking down, and a little wider from the corner of a sideways
       glance — the mimic that makes a turn read as a look */
    const past = (v: number, d: number) => (Math.abs(v) <= d ? 0 : (Math.sign(v) * (Math.abs(v) - d)) / (1 - d));
    const clamp1 = (v: number) => Math.max(-1, Math.min(1, v));
    const up = past(clamp1(-pose.pitch / 0.26 - pose.lookY / 7), 0.34);
    const side = Math.abs(past(clamp1(pose.lookX / 4.5), 0.4));
    const tall = Math.max(0.3, 1 + 0.55 * up - 0.1 * side);
    const wide = 1 - 0.05 * up + 0.12 * side;
    const sag = 0.5 + 0.5 * pose.breath;
    const e = 1 - pose.blink;
    for (const side of [-1, 1] as const) {
        const kOpen = wd * e,
            kShut = wd * (1 - e),
            kSleep = ws;
        const x0 = kOpen * 0.01 + kShut * 5.4 + kSleep * 6;
        const y0 = kOpen * 1.1 * tall + kShut * 0.6 + kSleep * (-1.4 + sag);
        const cy = kOpen * -3.3 * tall + kShut * 0.6 + kSleep * (5.4 + 2 * sag);
        const w = kOpen * EYE_RX * 2 * wide + kShut * 2.8 + kSleep * 4;
        /* the eyes drift toward the look when open, less so when shut */
        const dx = lx * (kOpen + 0.5 * kShut),
            dy = ly * (kOpen + 0.5 * kShut);
        at(side * half + dx, EYE_Y + dy, () => {
            ctx.strokeStyle = INK;
            ctx.lineWidth = w;
            ctx.stroke(eyePath(x0, y0, cy));
        });
    }
}
